import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Form } from "@/react/components/shared/uikit/Form";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { localeRoot } from "../../../.../../constants";
import { transformAmount } from "../../../../helpers/paywallCustomizationSerializer";

interface PriceFieldProps {
  index: number;
  name: string;
  transformValue: (input: any) => any;
}

const formRootName =
  "paywall_customization_attributes.reduced_price_properties.prices";

export const PriceField = ({
  name,
  index,
  transformValue,
}: PriceFieldProps) => {
  const beforeAmountFieldName = [formRootName, index, name].join(".");
  const currentAmountFieldName = [formRootName, index, `original_${name}`].join(
    ".",
  );
  const { watch, setError, clearErrors, getFieldState } = useFormContext();

  const beforeAmountValue = watch(beforeAmountFieldName);
  const currentAmountValue = watch(currentAmountFieldName);
  const fieldState = getFieldState(beforeAmountFieldName);

  useEffect(() => {
    const beforeAmountInteger = transformAmount(beforeAmountValue);
    const currentAmountInteger = transformAmount(currentAmountValue);

    if (beforeAmountInteger < currentAmountInteger) {
      setError(beforeAmountFieldName, {
        type: "manual",
        message: t([
          localeRoot,
          "builder.reduced_prices.fields.errors.before_price_higher_than_current_price",
        ]),
      });
    } else {
      clearErrors(beforeAmountFieldName);
    }
  }, [
    beforeAmountValue,
    currentAmountValue,
    beforeAmountFieldName,
    setError,
    clearErrors,
  ]);

  return (
    <>
      <Form.Input
        name={beforeAmountFieldName}
        wrapperClassName={classNames("col-span-4", {
          "ring-red-500": fieldState?.error,
        })}
        transformValue={transformValue}
        data-testid="reduced-price-original-amount-input"
      />
      <Icon type="24-arrow-right" />
      <Form.Input
        name={currentAmountFieldName}
        wrapperClassName="col-span-4"
        disabled
        transformValue={transformValue}
      />
      {fieldState?.error && (
        <span className="col-span-9">
          <Typography.BodySm color="text-feedback-error">
            {fieldState?.error?.message}
          </Typography.BodySm>
        </span>
      )}
    </>
  );
};
